// core
import { Component, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@open-portal-env';

// Campfire API Services
import {
  CustomerService,
  IntercomService,
  LoggerService,
  PartnerBrandService,
  PartnerService,
  QuoteService,
  ScriptLoaderService,
  UserService
} from '@campfire-nx/api';

// services
import { UserDataService } from '@open-portal-app/core/services/user-data.service';
import { PolicyDetailsService } from '@open-portal-app/core/services/policy-details.service';
import { AuthService } from '@open-portal-app/core/services/auth.service';

// constants
import { DASHBOARD_CONSTANTS } from '@open-portal-app/dashboard/constants/dashboard.constants';
import { HELP_CONSTANTS } from '@open-portal-app/help/constants/help.constants';
import { SCRIPTS_CONSTANTS } from '@open-portal-app/core/constants/scripts.contants';

@Component({
  selector: 'dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  userDetails!: { [key: string]: any };
  userData!: any;
  policies!: any[];
  allPolicies!: Array<any>;
  currentPolicies!: Array<any>;
  oldPolicies!: Array<any>;
  oldPolicyStatuses = ['cancelled', 'inactive', 'expired'];
  policyDisplayType = 'current';
  userDataLoading = false;
  overdueInvoices!: any[];
  partnerName: string = this.partnerBrandService.getAttribute('name', 'partner');
  partnerSlug: string = this.partnerBrandService.getAttribute('slug', 'partner');
  partner = this.partnerBrandService.data.partner;
  badgeStatus = HELP_CONSTANTS.policyBadgeStatus as any;
  productDisplaySections: Array<{ icon: string, class: string }> = [];
  quotableProductClasses!: { [index: string]: any };
  hasQuotableProductClasses = false;
  environment = environment;
  memberNumberLink = false;

  constructor(
    private router: Router,
    private userService: UserService,
    private userDataService: UserDataService,
    private policyDetailsService: PolicyDetailsService,
    private partnerBrandService: PartnerBrandService,
    private loggerService: LoggerService,
    private renderer: Renderer2,
    private scriptLoaderService: ScriptLoaderService,
    private authService: AuthService,
    private partnerService: PartnerService,
    private quoteService: QuoteService,
    private customerService: CustomerService,
    private intercomService: IntercomService
    ) { }

  async ngOnInit(): Promise<void> {
    try {
      this.userDataLoading = true;
      this.userData = await this.userDataService.getUserData();

      if (this.userData?.policies?.raptor.length) {
        this.partnerBrandService.setFlowBranding({
          partner: { slug: this.partnerBrandService.getAttribute('slug', 'partner') }
        });
      }
      this.userDetails = await this.getUserDetails();
      this.allPolicies = await this.getUserEntityList('policy');
      this.currentPolicies = this.allPolicies.filter((policy: any) => 
        this.oldPolicyStatuses.indexOf(policy.status) === -1);
      this.oldPolicies = this.allPolicies.filter((policy: any) =>
        this.oldPolicyStatuses.indexOf(policy.status) > -1);
      this.policies = this.currentPolicies;
      if (this.partnerSlug === 'avios') {
        this.getCustomerInformation();
      }

      this.setProductDisplaySections();
      this.overdueInvoices = this.getOverdueInvoices();
      this.getPartnerMultiProducts();
      this.loadCustomerIO(this.userDetails);
      this.userDataLoading = false;
      this.loggerService.info('DashboardComponent', 'ngOnInit');
    } catch (err) {
      this.userDataLoading = false;
      this.loggerService.error('DashboardComponent', 'ngOnInit', { error: err });
    }
  }

  async getUserEntityList(entityType: 'claim' | 'policy' | 'quote') {
    const entityList = [] as any[];
    const pluraliseEntityType = entityType === 'policy' ? 'policies' : `${entityType}s`;

    for (const [key, value] of Object.entries(this.userData[pluraliseEntityType])) {
      const entity: any = value;

      if (entityType === 'policy' && entity && entity.length > 0) {
        // SALES-2393: Filter out linked policies and format remaining policies
        const filteredPolicies = entity.filter((item: any) => item.product?.class !== 'linked');
        const promises = filteredPolicies.map((item: any) => this.getFormattedPolicy(item));
        const results = await Promise.all(promises);
        const validResults = results.filter(result => result !== undefined);
        entityList.push(...validResults);
      }
    }

    return entityList;
  }

  async getFormattedPolicy(policy: any) {
    const claims = this.userData.claims;
    return this.policyDetailsService.formatPolicy(policy, this.userData)
    .then((response: any) => {
      return {
        slug: response?.slug,
          policyTitle: response.policyTitle,
          class: policy.product?.class,
          status: response?.policyState,
          policyTypeLabel: response?.policyTypeLabel,
          overdueInvoices: response?.overdueInvoices,
          claims: claims?.length && !!claims
            .find((claim: any) => { return claim.policy_slug === response?.slug && ['L', 'Q'].indexOf(claim.state) > -1; })
        };
      })
      .catch((err) => {
        this.loggerService.error('DashboardComponent', 'processPolicy', { error: err });
      })
  }

  async getUserDetails() {
    return this.userService.details()
      .then((response: any) => {
        const intercomData = this.partnerBrandService.getAttribute('intercom', 'partner');

        if (intercomData?.intercomAppId) {
          // When userdata becomes available re-initiate intercom service with user data
          this.intercomService.addIntercomScripts(
            intercomData,
            {
              userSlug: response.slug || response.uuid,
              name: `${ response.first_name } ${ response.last_name }`,
              email: `${ response.email }`,
              mobile: `${ response.mobile }`,
              createdAt: `${ Math.floor(new Date(response.date_joined).getTime() / 1000) }`,
              userHash: `${ response.hmac }`,
            }
          );
        }
        return response;
      })
      .catch((err) => {
        this.loggerService.error('DashboardComponent', 'getUserDetails', { error: err });
      });
  }

  goToPolicy(slug: string): void {
    const policy = this.policies.find((policy) => policy.slug === slug || policy.uuid === slug);

    this.loggerService.info('DashboardComponent', 'goToPolicy', { policy: slug });
    this.router.navigate([`policy/${policy.class}/${slug}`]);
  }

  redirectToBaseDomain(): void {
    const companyInfo = this.partnerBrandService.getAttribute('companyInfo', 'partner');

    window.open(`https://${companyInfo.BASE_DOMAIN}`, '_self');
  }

  getOverdueInvoices() {
    return this.policies?.filter(policy => policy.overdueInvoices && policy.overdueInvoices.count > 0) || [];
  }

  retryPayment(policySlug: string) {
    this.loggerService.info('DashboardComponent', 'retryPayment', { policy: policySlug }, 'Clicked Retry payment button');
    this.router.navigate([`/policy/payment/retry/${policySlug}`]);
  }

  getPoliciesCount(productClass : string) {
    let policyCount = 0;

    if (this.policies && this.policies.length > 0) {
      const relevantPolicies = this.policies?.filter(policy => policy.class === productClass) || [];
      policyCount = relevantPolicies.length;
    }
    return policyCount;
  }

  createQuote(productClass: string, multiProduct: string) {
    const newQuoteWindow = window.open('', '_blank');
    // Rage click prevention: Create quote only when creatingQuote is not already in progress
    if (!this.quotableProductClasses[productClass].creatingQuote) {
      this.quotableProductClasses[productClass].creatingQuote = true;
      this.quoteService.prepareQuote({
        product_code: multiProduct,
        details: { prefill: { is_required: true } }
      })
        .then((response: any) => {
          if (newQuoteWindow) {
            newQuoteWindow.location.href = `/quote/${response.quote_uuid}`;
          }
          this.quotableProductClasses[productClass].creatingQuote = false;
        })
        .catch((err: any) => {
          this.loggerService.error('DashboardComponent', 'createQuote', { error: err });
          this.quotableProductClasses[productClass].creatingQuote = false;
        });
    }
  }

  loadCustomerIO(userData: any) {
    const customerIOScript = SCRIPTS_CONSTANTS.customerIO;
    const isScriptLoaded = document.getElementById('cio-tracker');

    if (!isScriptLoaded && userData.cio_id) {
      customerIOScript.text = customerIOScript.text.replace('{{ cio_data_site_id }}', environment.cioDataSiteId);
      customerIOScript.text = customerIOScript.text.replace('{{ cio_id }}', userData.cio_id);
      this.scriptLoaderService.loadJsScript(
        this.renderer,
        customerIOScript.text,
        customerIOScript.type
      );
    }
  }

  getPolicyDisplayTypes() {
    if (this.policyDisplayType === 'old') {
      this.policies = this.currentPolicies;
      this.policyDisplayType = 'current';
    } else {
      this.policies = this.oldPolicies;
      this.policyDisplayType = 'old';
    }

    this.setProductDisplaySections();

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  getCustomerInformation() {
    this.customerService.details()
      .then((response: any) => {
        if (!response.avios_member_number) {
          // show the link to the member number flow
          this.memberNumberLink = true;
        }
      });
  }

  setProductDisplaySections() {
    this.productDisplaySections = [];
    for (const policy of this.policies) {
      // Detecting unique product classes for which customer has policies
      if (
        policy.class &&
        !this.productDisplaySections.find(section => section.class === policy.class)
      ) {
        this.productDisplaySections.push({
          icon: this.getProductIcon(policy.class),
          class: policy.class
        });
      }
    }
  }

  getPartnerMultiProducts() {
    const multiProducts = this.partnerBrandService.getAttribute('multiProducts', 'partner');

    if (!multiProducts) {
      this.partnerService.detail(
        this.authService.getApiKey() ||
        this.partnerBrandService.getAttribute('slug', 'partner')
      )
        .then((response: any) => {
          this.partnerBrandService.setAttribute('multiProducts', response.multi_products, 'partner');
          this.setQuotableProductClasses(response.multi_products);
        })
        .catch((err: any) => {
          this.loggerService.error('DashboardComponent', 'getPartnerMultiProducts', { error: err });
        })
    } else {
      this.setQuotableProductClasses(multiProducts);
    }
  }

  setQuotableProductClasses(multiProducts: any) {
    this.quotableProductClasses = {};

    // set a product class as quotable only if it's multi-product has cross-sell enabled
    for (const key in multiProducts) {
      if (
        multiProducts[key].class &&
        multiProducts[key]?.cross_sell?.is_enabled &&
        !this.quotableProductClasses[multiProducts[key].class]
      ) {
        this.quotableProductClasses[multiProducts[key].class] = {
          multiProduct: key,
          title: multiProducts[key].cross_sell.title || DASHBOARD_CONSTANTS.crossSellDefaultCopy.title,
          text: multiProducts[key].cross_sell.text || DASHBOARD_CONSTANTS.crossSellDefaultCopy.text,
          actionText: multiProducts[key].cross_sell.actionText || DASHBOARD_CONSTANTS.crossSellDefaultCopy.actionText,
          icon: this.getProductIcon(multiProducts[key].class)
        };
      }
    }
    this.hasQuotableProductClasses = this.quotableProductClasses &&
      Object.keys(this.quotableProductClasses).length > 0;
  }

  getProductIcon(productClass: 'car' | 'home' | 'travel' | 'mobile' | 'contents' | '') {
    const icon = {
      car: 'options-hirecar-small',
      home: 'house-home-small',
      travel: 'travel-small',
      mobile: 'marketing-optionals',
      contents: 'stuff-landlordscontents'
    };

    return productClass ? icon[productClass] : '';
  }
}
